// Copyright (c) 2024 The Bitcoin developers
// Distributed under the MIT software license, see the accompanying
// file COPYING or http://www.opensource.org/licenses/mit-license.php.

import React, { useState } from 'react';
import { WalletContext, isWalletContextLoaded } from 'wallet/context';
import PrimaryButton, { SecondaryButton } from 'components/Common/Buttons';
import { Event } from 'components/Common/GoogleAnalytics';
import { validateMnemonic } from 'validation';
import appConfig from 'config/app';
import { createCashtabWallet, generateMnemonic } from 'wallet';
import { WelcomeCtn, WelcomeLink, WelcomeText } from './styles';
import Modal from 'components/Common/Modal';
import { ModalTextArea } from 'components/Common/Inputs';
import CashtabLogo from 'assets/cashtab_xec.png';

const OnBoarding = () => {
    const ContextValue = React.useContext(WalletContext);
    const [importedMnemonic, setImportedMnemonic] = useState<string>('');
    const [showImportWalletModal, setShowImportWalletModal] =
        useState<boolean>(false);
    const [isImportingWallet, setIsImportingWallet] = useState<boolean>(false);
    // Initialize as true so that validation error only renders after user input
    const [isValidMnemonic, setIsValidMnemonic] = useState<boolean>(true);

    if (!isWalletContextLoaded(ContextValue)) {
        return null;
    }
    const { saveWallet } = ContextValue;

    async function importWallet(): Promise<void> {
        if (isImportingWallet) {
            return;
        }
        setIsImportingWallet(true);
        // Event("Category", "Action", "Label")
        // Track number of created wallets from onboarding
        Event('Onboarding.js', 'Create Wallet', 'Imported');
        try {
            const importedWallet = createCashtabWallet(importedMnemonic);
            await saveWallet(importedWallet, importedWallet.address);
            // Close the modal
            setShowImportWalletModal(false);
        } catch (err) {
            console.error('Error importing wallet from onboarding', err);
        } finally {
            setIsImportingWallet(false);
        }
    }

    async function createNewWallet(): Promise<void> {
        // Event("Category", "Action", "Label")
        // Track number of created wallets from onboarding
        Event('Onboarding.js', 'Create Wallet', 'New');
        const newWallet = createCashtabWallet(generateMnemonic());
        try {
            await saveWallet(newWallet, newWallet.address);
        } catch (err) {
            console.error('Error creating wallet from onboarding', err);
        }
    }

    const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const { value } = e.target;

        // Validate mnemonic on change
        // Import button should be disabled unless mnemonic is valid
        setIsValidMnemonic(validateMnemonic(value));

        setImportedMnemonic(value);
    };

    return (
        <>
            {showImportWalletModal && (
                <Modal
                    title={`Import wallet`}
                    handleOk={importWallet}
                    handleCancel={() => {
                        setShowImportWalletModal(false);
                        setIsImportingWallet(false);
                    }}
                    showCancelButton
                    disabled={
                        !isValidMnemonic ||
                        importedMnemonic === '' ||
                        isImportingWallet
                    }
                    isConfirmLoading={isImportingWallet}
                >
                    <ModalTextArea
                        placeholder="mnemonic (seed phrase)"
                        name="mnemonic"
                        value={importedMnemonic}
                        error={
                            isValidMnemonic
                                ? false
                                : 'Invalid 12-word mnemonic. Note: all letters must be lowercase.'
                        }
                        handleInput={handleInput}
                        height={96}
                        spellCheck={false}
                        autoCorrect="off"
                        autoCapitalize="off"
                    />
                </Modal>
            )}

            <WelcomeCtn>
                <img src={CashtabLogo} alt="cashtab" />
                <div>
                    <WelcomeText>
                        Welcome to Cashtab! The{' '}
                        <WelcomeLink
                            href="https://github.com/bitcoin-abc/bitcoin-abc"
                            target="_blank"
                            rel="noreferrer"
                        >
                            open source,
                        </WelcomeLink>{' '}
                        non-custodial web wallet for {appConfig.name}.
                    </WelcomeText>

                    <PrimaryButton onClick={() => createNewWallet()}>
                        New Wallet
                    </PrimaryButton>

                    <SecondaryButton
                        type="button"
                        onClick={() => setShowImportWalletModal(true)}
                    >
                        Import Wallet
                    </SecondaryButton>
                </div>
            </WelcomeCtn>
        </>
    );
};

export default OnBoarding;
